<template>
    <VForm
        v-slot="{ errors }"
        @submit="onSubmit">
        <div class="grid sm:grid-cols-2 gap-6">
            <template v-if="invitingOrganisation">
                <BaseText class="sm:col-span-2">
                    {{ $t('auth.organisationInvite', {organisation: invitingOrganisation.name}) }}
                </BaseText>
            </template>

            <BaseInput
                v-model="model.firstname"
                :label="$t('auth.firstname')"
                rules="required|max:255"
                required
                autocomplete="given-name"
                name="register-firstname" />
            <BaseInput
                v-model="model.lastname"
                :label="$t('auth.lastname')"
                rules="required|max:255"
                required
                autocomplete="family-name"
                name="register-lastname" />

            <BaseDatePicker
                v-model="model.birthday"
                class="sm:col-span-2"
                :label="$t('auth.birthday')"
                rules="required"
                required
                name="register-birthday" />

            <BaseInput
                v-model="model.email"
                class="sm:col-span-2"
                :label="$t('auth.email')"
                rules="required|email|max:255"
                required
                autocomplete="username"
                type="email"
                name="register-email" />

            <div class="sm:col-span-2">
                <BaseInput
                    v-model="model.password"
                    :label="$t('auth.password')"
                    type="password"
                    name="register-password"
                    rules="required|verify_password"
                    autocomplete="new-password"
                    required />
                <PasswordStrengthMeter
                    :model-value="model.password"
                    show-hints />
            </div>

            <BaseInput
                v-model="model.password_confirmation"
                class="sm:col-span-2"
                :label="$t('auth.repeatPassword')"
                :rules="{required: true, confirm_password: model.password}"
                required
                type="password"
                autocomplete="new-password"
                name="reset-password-confirmation" />

            <template v-if="!invitingOrganisation">
                <div class="sm:col-span-2">
                    <BaseInput
                        v-model="model.organisation_name"
                        :label="$t('auth.organisationName')"
                        rules="required|max:255"
                        required
                        name="register-organisation_name" />
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {{ $t('auth.organisationNameHint') }}
                    </p>
                </div>

                <BaseSelect
                    v-model="model.organisation_sport_id"
                    class="sm:col-span-2"
                    :label="$t('auth.organisationSport')"
                    spa-route="auth.sports.index"
                    display-key="name"
                    identifier="id"
                    spa-prefetch
                    rules="required"
                    required
                    name="register-organisation_sport_id" />
            </template>

            <BaseButton
                class="w-full sm:col-span-2"
                color="primary"
                :disabled="loading || !!Object.keys(errors).length"
                type="submit">
                <span v-if="!loading">{{ $t('auth.register') }}</span>
                <span
                    v-else
                    class="flex items-center gap-2">
                    <LoadingSpinner />
                    {{ $t('auth.pleaseWait') }}
                </span>
            </BaseButton>

            <div class="text-center sm:col-span-2">
                <p class="text-sm text-gray-500">
                    {{ $t('auth.alreadyHaveAccount') }}
                    <span
                        class="font-semibold text-primary-600 hover:text-primary-500 cursor-pointer"
                        @click="$emit('authPageVisible', AuthLogin)">
                        {{ $t('auth.signIn') }}
                    </span>
                </p>
            </div>
        </div>
    </VForm>
</template>

<script setup lang="ts">
import AuthLogin from "@/components/pages/auth/AuthLogin.vue"
import {useAuthStore} from '~/store/auth'

const authStore = useAuthStore()
const notification = useNotification()
const {$apiRoute} = useNuxtApp()
const {t: $t} = useI18n()
const route = useRoute()

defineProps({
    invitingOrganisation: {type: Object, required: false, default: () => null}
})

defineEmits(['authPageVisible'])

const model = ref({
    organisation_name: null as string | null,
    organisation_sport_id: null as number | null,
    organisation_token: route.query.invitationToken as string | undefined,
    firstname: null as string | null,
    lastname: null as string | null,
    birthday: null as string | null,
    email: null as string | null,
    password: null as string | null,
    password_confirmation: null as string | null,
})

const loading = ref(false)

async function onSubmit() {
    loading.value = true
    const response = await $lara.post($apiRoute('auth.register'), {body: model.value})
    loading.value = false

    if (response?.error) return notification.error(response.error.response._data?.message)
    await login()
    notification.success($t('users.welcome', {name: model.value.firstname}))
}

async function login() {
    loading.value = true
    const response = await $lara.post('/app/spa/login', {body: model.value})
    loading.value = false
    if (response?.error) return notification.error(response.error.response._data?.message)
    await authStore.successfulLogin('/service/dashboard', {celebrate: 1})
}
</script>